/**
 * React hooks and components for TagadaPay Plugin SDK v2
 * Uses core functions for business logic
 */

// Providers
export { ExpressPaymentMethodsProvider } from './providers/ExpressPaymentMethodsProvider';
export { TagadaProvider, useTagadaContext } from './providers/TagadaProvider';
export type { DebugScript } from './providers/TagadaProvider';

// Components
export { ApplePayButton } from './components/ApplePayButton';
export { StripeExpressButton } from './components/StripeExpressButton';
export { GooglePayButton } from './components/GooglePayButton';
export { PreviewModeIndicator } from './components/PreviewModeIndicator';
export { WhopCheckout } from './components/WhopCheckout';

// Hooks
export { useApplePayCheckout } from './hooks/useApplePayCheckout';
export { useAuth } from './hooks/useAuth';
export { useCheckoutToken } from './hooks/useCheckoutToken';
export { useClubOffers } from './hooks/useClubOffers';
export { useCredits } from './hooks/useCredits';
export { useCustomer } from './hooks/useCustomer';
export { useCustomerInfos } from './hooks/useCustomerInfos';
export { useCustomerOrders } from './hooks/useCustomerOrders';
export { useCustomerSubscriptions } from './hooks/useCustomerSubscriptions';
export { useExpressPaymentMethods } from './hooks/useExpressPaymentMethods';
export { useGeoLocation } from './hooks/useGeoLocation';
export { useGoogleAutocomplete } from './hooks/useGoogleAutocomplete';
export { useGooglePayCheckout } from './hooks/useGooglePayCheckout';
export {
    getAvailableLanguages, useCountryOptions, useISOData, useLanguageImport, useRegionOptions
} from './hooks/useISOData';
export { useLogin } from './hooks/useLogin';
export { PixelTrackingProvider, usePixelTracking } from './hooks/usePixelTracking';
export type { PixelTrackingContextValue, StandardPixelEvent } from './hooks/usePixelTracking';
export { usePluginConfig } from './hooks/usePluginConfig';
export { useRemappableParams } from './hooks/useRemappableParams';

// TanStack Query hooks (recommended)
export { queryKeys, useApiMutation, useApiQuery, useInvalidateQuery, usePreloadQuery } from './hooks/useApiQuery';
export { useCheckoutQuery as useCheckout } from './hooks/useCheckoutQuery';
export { useCurrency } from './hooks/useCurrency';
export { useDiscountsQuery as useDiscounts } from './hooks/useDiscountsQuery';
export { useOfferQuery as useOffer } from './hooks/useOfferQuery';
export { useOrderBumpQuery as useOrderBump } from './hooks/useOrderBumpQuery';
export { useOrderQuery as useOrder } from './hooks/useOrderQuery';
export { usePaymentQuery as usePayment } from './hooks/usePaymentQuery';
export { usePaymentRetrieve } from './hooks/usePaymentRetrieve';
export { usePostPurchasesQuery as usePostPurchases } from './hooks/usePostPurchasesQuery';
export { usePreviewOffer } from './hooks/usePreviewOffer';
export { useProductsQuery as useProducts } from './hooks/useProductsQuery';
export { usePromotionsQuery as usePromotions } from './hooks/usePromotionsQuery';
export { useSetPaymentMethod } from './hooks/useSetPaymentMethod';
export { useShippingRatesQuery as useShippingRates } from './hooks/useShippingRatesQuery';
export { useStoreConfigQuery as useStoreConfig } from './hooks/useStoreConfigQuery';
export { useThreeds } from './hooks/useThreeds';
export { useThreedsModal } from './hooks/useThreedsModal';
export { useTranslation } from './hooks/useTranslation';
export { useVipOffersQuery as useVipOffers } from './hooks/useVipOffersQuery';
export { useWhopPaymentPolling } from './hooks/useWhopPaymentPolling';

// Funnel hooks
export { useFunnel } from './hooks/useFunnel';

// Step config hook (access runtime configuration from HTML injection)
export { useStepConfig } from './hooks/useStepConfig';

// Legacy funnel hooks (deprecated - use TagadaProvider + useFunnel instead)
export { useFunnel as useFunnelLegacy, useSimpleFunnel } from './hooks/useFunnelLegacy';

// Types
export type { UseApplePayCheckoutOptions } from './hooks/useApplePayCheckout';
export type { UseCheckoutTokenOptions, UseCheckoutTokenResult } from './hooks/useCheckoutToken';
export type { ClubOffer, ClubOfferItem, ClubOfferLineItem, ClubOfferSummary, UseClubOffersOptions, UseClubOffersResult } from './hooks/useClubOffers';
export type { UseCreditsOptions, UseCreditsResult } from './hooks/useCredits';
export type { UseCustomerResult } from './hooks/useCustomer';
export type { UseCustomerInfosOptions, UseCustomerInfosResult } from './hooks/useCustomerInfos';
export type { UseCustomerOrdersOptions, UseCustomerOrdersResult } from './hooks/useCustomerOrders';
export type { UseCustomerSubscriptionsOptions, UseCustomerSubscriptionsResult } from './hooks/useCustomerSubscriptions';
export type {
    ExpressPaymentMethodsContextType,
    ExpressPaymentMethodsProviderProps
} from './hooks/useExpressPaymentMethods';
export type { UseGooglePayCheckoutOptions } from './hooks/useGooglePayCheckout';
export type { UseLoginOptions, UseLoginResult } from './hooks/useLogin';

// Component types
export type { ApplePayButtonProps } from './components/ApplePayButton';
export type { StripeExpressButtonProps } from './components/StripeExpressButton';
export type { GooglePayButtonProps } from './components/GooglePayButton';
export type { PreviewModeIndicatorProps } from './components/PreviewModeIndicator';
export type { WhopCheckoutHandle, WhopCheckoutProps } from './components/WhopCheckout';
export type { WhopPayment, UseWhopPaymentPollingOptions, UseWhopPaymentPollingReturn } from './hooks/useWhopPaymentPolling';
export type {
    GeoLocationData, UseGeoLocationOptions,
    UseGeoLocationReturn
} from './hooks/useGeoLocation';
export type {
    ExtractedAddress, GooglePlaceDetails, GooglePrediction, UseGoogleAutocompleteOptions,
    UseGoogleAutocompleteResult
} from './hooks/useGoogleAutocomplete';
export type {
    ISOCountry,
    ISORegion, UseISODataResult
} from './hooks/useISOData';
export type { UsePluginConfigOptions, UsePluginConfigResult } from './hooks/usePluginConfig';
export type { TranslateFunction, UseTranslationOptions, UseTranslationResult } from './hooks/useTranslation';

// TanStack Query types
export { FunnelActionType } from '../core/resources/funnel';
export type { CheckoutMessages, UseCheckoutQueryOptions as UseCheckoutOptions, UseCheckoutQueryResult as UseCheckoutResult } from './hooks/useCheckoutQuery';
export type { UseDiscountsQueryOptions as UseDiscountsOptions, UseDiscountsQueryResult as UseDiscountsResult } from './hooks/useDiscountsQuery';

// Funnel types
export type {
    FunnelAction,
    FunnelNavigationAction,
    FunnelNavigationResult,
    SimpleFunnelContext
} from '../core/resources/funnel';
export type { FunnelContextValue, StepConfigValue } from './hooks/useFunnel';
export type { UseStepConfigResult } from './hooks/useStepConfig';
export type { RuntimeStepConfig, PaymentMethodConfig, PaymentSetupConfig, PaymentSetupMethod } from '../core/funnelClient';
export { getEnabledMethods, getExpressMethods, getExpressMethodsByProcessor, findMethod, isMethodEnabled } from '../core/funnelClient';

// Legacy funnel types (deprecated)
export type {
    UseFunnelOptions as UseFunnelLegacyOptions,
    UseFunnelResult as UseFunnelLegacyResult
} from './hooks/useFunnelLegacy';
export type { AvailableVariant, LineItemSelection, OfferLineItem, OfferPreviewSummary, UseOfferQueryOptions as UseOfferOptions, UseOfferQueryResult as UseOfferResult } from './hooks/useOfferQuery';
export type { UseOrderBumpQueryOptions as UseOrderBumpOptions, UseOrderBumpQueryResult as UseOrderBumpResult } from './hooks/useOrderBumpQuery';
export type { UseOrderQueryOptions as UseOrderOptions, UseOrderQueryResult as UseOrderResult } from './hooks/useOrderQuery';
export type { PaymentHook as UsePaymentResult } from './hooks/usePaymentQuery';
export type { PaymentRetrieveHook as UsePaymentRetrieveResult } from './hooks/usePaymentRetrieve';
export type { UsePostPurchasesQueryOptions as UsePostPurchasesOptions, UsePostPurchasesQueryResult as UsePostPurchasesResult } from './hooks/usePostPurchasesQuery';
export type { PreviewOfferSummary, UsePreviewOfferOptions, UsePreviewOfferResult } from './hooks/usePreviewOffer';
export type { UseProductsQueryOptions as UseProductsOptions, UseProductsQueryResult as UseProductsResult } from './hooks/useProductsQuery';
export type { UsePromotionsQueryOptions as UsePromotionsOptions, UsePromotionsQueryResult as UsePromotionsResult } from './hooks/usePromotionsQuery';
export type { UseSetPaymentMethodOptions, UseSetPaymentMethodResult } from './hooks/useSetPaymentMethod';
export type { UseShippingRatesQueryOptions as UseShippingRatesOptions, UseShippingRatesQueryResult as UseShippingRatesResult } from './hooks/useShippingRatesQuery';
export type { UseStoreConfigQueryOptions as UseStoreConfigOptions, UseStoreConfigQueryResult as UseStoreConfigResult } from './hooks/useStoreConfigQuery';
export type {
    PaymentInstrument, ThreedsChallenge, ThreedsHook, ThreedsOptions, ThreedsProvider, ThreedsSession
} from './hooks/useThreeds';
export type { UseVipOffersQueryOptions as UseVipOffersOptions, UseVipOffersQueryResult as UseVipOffersResult } from './hooks/useVipOffersQuery';

// Re-export utilities from main react
export { formatMoney } from '../../react/utils/money';

// Error types
export {
    TagadaError,
    TagadaApiError,
    TagadaNetworkError,
    TagadaAuthError,
    TagadaValidationError,
    TagadaCircuitBreakerError,
    TagadaErrorCode,
} from '../core/errors';
export type { TagadaErrorOptions, TagadaErrorCodeValue } from '../core/errors';

// Core domain types
export type { PaymentMethodName } from '../core/resources/checkout';
export type {
    Order,
    OrderItem as CoreOrderItem,
    OrderAddress,
    OrderSummary,
    OrderWithRelations,
    PaymentSummary,
    PromotionSummary,
    Subscription,
    DeviceInfo,
    PromotionCode,
    Customer,
    Store,
    PickupPoint,
} from '../core/types';

// Alias for compatibility
export type OrderItem = import('../core/utils/order').OrderLineItem;
